import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Drawer,
  Card,
  Button,
  Typography,
  Spin,
  List,
  Tag,
  Space,
  Dropdown,
  Menu,
  message,
  Collapse,
} from "antd";
import { SEARCH_BAR } from "../App";
import {
  FileTextOutlined,
  ReloadOutlined,
  MessageOutlined,
  ShareAltOutlined,
  LinkOutlined,
  FacebookOutlined,
  TwitterOutlined,
  LinkedinOutlined,
} from "@ant-design/icons";
import MarkdownPreview from "@uiw/react-markdown-preview";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import { ChartsAndTables, CompanyCharts } from "../ChartsTables";
import "../App.css";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { Helmet } from "react-helmet";
import { Input } from "antd";
import { useParams } from "react-router-dom";
import CarouselSwiper from "../Carousels";
import { LeftOutlined, RightOutlined } from "@ant-design/icons"; // 👈 Ant icons

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const isMobile = window.innerWidth < 768;

export const API_URL =
  "https://ixvr1ju471.execute-api.us-east-1.amazonaws.com/prod";

const FaqCollapse = ({ faqContent }) => {
  if (!faqContent) return null;

  let faqs = [];
  try {
    // Extract questions and answers from <faq1>, <faq2>, <faq3>
    const faqRegex =
      /<faq\d+>\s*<question>(.*?)<\/question>\s*<answer>(.*?)<\/answer>\s*<\/faq\d+>/gs;
    faqs = [...faqContent.matchAll(faqRegex)].map((match) => ({
      question: match[1].trim(),
      answer: match[2].trim(),
    }));
  } catch (error) {
    console.error("Failed to parse FAQs:", error);
  }

  if (faqs.length === 0) return null;

  return (
    <Card
      style={{
        marginTop: "3rem",
        padding: "2rem",
        backgroundColor: "#FAFAFA",
        borderRadius: "16px",
        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.06)",
        border: "1px solid #E0E0E0",
      }}
      bodyStyle={{ padding: "0" }}
    >
      <div style={{ padding: "0 24px 24px 24px" }}>
        <h2
          style={{
            fontSize: "24px",
            fontWeight: 800,
            color: "#0D47A1",
            marginBottom: "1rem",
            borderBottom: "2px solid #BBDEFB",
            paddingBottom: "0.3rem",
          }}
        >
          📚 Frequently Asked Questions
        </h2>
        <Collapse
          accordion
          ghost
          expandIconPosition="end"
          style={{ background: "transparent" }}
          defaultActiveKey={[0]} // ✅ open first FAQ by default
        >
          {faqs.map((faq, index) => (
            <Panel
              header={
                <span style={{ fontWeight: 600, fontSize: "1.05rem" }}>
                  {faq.question}
                </span>
              }
              key={index}
            >
              <Paragraph
                style={{ fontSize: "1rem", lineHeight: "1.7", margin: 0 }}
              >
                <div style={{ fontSize: "1rem", lineHeight: "1.7" }}>
                  <MarkdownPreview source={faq.answer} />
                </div>{" "}
              </Paragraph>
            </Panel>
          ))}
        </Collapse>
      </div>
    </Card>
  );
};

export const fetchBlogSectionsBySymbol = async (symbol, setBlogSections) => {
  try {
    const symbolUpper = symbol?.toUpperCase();

    const response = await axios.post(
      API_URL,
      {
        queryStringParameters: { symbol: symbolUpper },
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    const parsed = JSON.parse(response.data.body);
    if (!parsed?.details) return;

    console.log(parsed);
    const sectionMap = {
      self: [],
      competitors: [],
      sector: [],
    };

    const capitalizeWords = (str) =>
      str
        .replace(/-/g, " ")
        .replace(/[^\w\s]/gi, "")
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .replace(/\s+/g, " ")
        .trim();

    const getVariant = (category) => {
      if (category === "multiple_company") return "competitors";
      if (category === "industry") return "sector";
      return "self";
    };

    const rawDetails = parsed.details;
    const blogItems =
      typeof rawDetails === "string" ? JSON.parse(rawDetails) : rawDetails;

    let tagCounter = 0;

    blogItems.forEach((item) => {
      const variant = getVariant(item.category);
      let finalTitle = "";

      if (variant === "sector") {
        const industrySlug = item.url.split("/")[0];
        finalTitle = capitalizeWords(industrySlug);
      } else if (
        variant === "competitors" &&
        Array.isArray(item.symbol) &&
        item.symbol.length === 2
      ) {
        const [s1, s2] = item.symbol.map((s) => s.toUpperCase());
        finalTitle = `Compare ${s1} vs ${s2}`;
      } else {
        finalTitle = capitalizeWords(
          item.url.split("/").pop().replace(/3f$/i, "?")
        );

        const symbolList = Array.isArray(item.symbol)
          ? item.symbol
          : [item.symbol];
        symbolList.forEach((s) => {
          const upper = s?.toUpperCase();
          // finalTitle = finalTitle.replace(new RegExp(s, "ig"), upper);
        });
      }

      const url = `https://www.stockbuzz.ai/blogs/${symbol}/${item.url}`;

      const tagStart =
        variant === "sector"
          ? 0
          : tagCounter % Math.max(1, item.tags.length - 1);
      const tagEnd = variant === "sector" ? 4 : tagStart + 2;
      const selectedTags = item.tags.slice(tagStart, tagEnd);
      tagCounter++;

      // ✨ Extract <headline> preview if present

      let preview = "";
      const faqHeadlineRaw = item?.faq_headline || "";

      if (faqHeadlineRaw?.includes("<headline>")) {
        const parts = faqHeadlineRaw.split("<headline>");
        preview = parts[1]
          ? parts[1]
              .split("\n")
              .find((line) => line.trim() !== "")
              .trim()
          : "";
      }

      console.log(blogItems);

      sectionMap[variant].push({
        title: finalTitle,
        url,
        preview,
        lastUpdated: new Date(
          Date.now() - Math.floor(Math.random() * 10 * 86400000)
        )
          .toISOString()
          .split("T")[0],
        companies: variant === "self" ? [] : item.company_name,
        tags: selectedTags,
      });
    });

    // ✨ REMOVE DUPLICATES: based on URL
    const deduplicateCards = (cards) => {
      const seen = new Set();
      return cards.filter((card) => {
        if (seen.has(card.url)) return false;
        seen.add(card.url);
        return true;
      });
    };

    const result = [];
    if (sectionMap.self.length) {
      result.push({
        heading: `More research on ${symbolUpper}`,
        variant: "self",
        cards: deduplicateCards(sectionMap.self),
      });
    }

    if (sectionMap.competitors.length) {
      result.push({
        heading: "Blogs Related to Competitors",
        variant: "competitors",
        cards: deduplicateCards(sectionMap.competitors),
      });
    }

    if (sectionMap.sector.length) {
      result.push({
        heading: "Sector Analysis",
        variant: "sector",
        cards: deduplicateCards(sectionMap.sector),
      });
    }

    setBlogSections(result);
  } catch (err) {
    console.error("Failed to fetch blog sections", err);
  }
};

export const BlogCarouselContainer = () => {
  const { symbol } = useParams();
  const [blogSections, setBlogSections] = useState([]);

  useEffect(() => {
    if (symbol) {
      fetchBlogSectionsBySymbol(symbol, setBlogSections);
    }
  }, [symbol]);

  return (
    <>
      {blogSections?.map((section, idx) => (
        <CarouselSwiper
          key={idx}
          heading={section.heading}
          cards={section.cards}
          variant={section.variant}
        />
      ))}
    </>
  );
};

// 📌 SQL Query Formatter
export const ClickableTags = ({ tags }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleIndustryCardClick = async (message) => {
    const slug = message.toLowerCase().replace(/\s+/g, "-");
    const blogPath = `/blogs/${slug}/sector-analysis-of-${slug}-and-what-it-means-for-investors`;
    const fallbackParams = new URLSearchParams({
      question: `Analyze Top Companies in ${message} Sector`,
    }).toString();

    try {
      const resp = await axios.post(
        API_URL,
        {
          queryStringParameters: {
            url: `sector-analysis-of-${slug}-and-what-it-means-for-investors`,
          },
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const raw = JSON.parse(resp.data.body);
      if (raw?.description) {
        // open in new tab if SEO data exists
        window.open(blogPath, "_blank");
        return;
      }
    } catch (err) {
      console.warn("SEO check error, will fallback", err);
    }

    // fallback opens search page in new tab
    window.open(`/?${fallbackParams}`, "_blank");
  };

  return (
    <div style={styles.tagsContainer}>
      <Space wrap>
        {tags.map((tag, index) => (
          <Tag
            key={index}
            style={styles.tag}
            onClick={() => handleIndustryCardClick(tag)}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#e6f7ff")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = "#d7e3fc")}
          >
            {tag.charAt(0).toUpperCase() + tag.slice(1)}
          </Tag>
        ))}
      </Space>
    </div>
  );
};

const formatSQLQuery = (query) =>
  query
    .replace(/\s+/g, " ")
    .replace(
      /\b(SELECT|FROM|WHERE|GROUP BY|ORDER BY|LIMIT|INNER JOIN|LEFT JOIN|RIGHT JOIN|FULL JOIN|UNION|HAVING|ON|WITH|AS|INSERT INTO|VALUES|UPDATE|SET|DELETE)\b/gi,
      "\n$1"
    )
    .replace(/,(?!\s)/g, ", ")
    .trim();

// 📌 MathJax Configuration
const mathJaxConfig = {
  loader: { load: ["input/tex", "output/chtml"] },
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
  },
  chtml: { scale: 0.9, minScale: 0.7 },
};

const pastelColors = [
  "#FDE2E4", // Soft Red
  "#FAD2E1", // Light Pink
  "#E2ECE9", // Mint Green
  "#D7E3FC", // Light Blue
  "#EAE4E9", // Soft Purple
];

// const swiperParams = {
//   spaceBetween: 20,
//   centeredSlides: isMobile,
//   breakpoints: {
//     576: { slidesPerView: 1 },
//     992: { slidesPerView: 3.6 },
//     1200: { slidesPerView: 5 },
//   },
// };

const swiperParams = {
  spaceBetween: 20,
  slidesPerView: 2.2, // 👈 Always show at least 2 cards even on mobile
  centeredSlides: false,
  breakpoints: {
    0: { slidesPerView: 2.2 }, // 👈 Mobile: 2.2 cards visible
    576: { slidesPerView: 3 }, // 👈 Small tablets: 3 cards
    768: { slidesPerView: 4 }, // 👈 iPad portrait: 4 cards
    992: { slidesPerView: 5 }, // 👈 Desktop
    1200: { slidesPerView: 6 }, // 👈 Large screens
  },
};

const shareOnSocial = (platform) => {
  const url = encodeURIComponent(window.location.href);
  const ogTitle =
    document
      .querySelector('meta[property="og:title"]')
      ?.getAttribute("content") ||
    "Check out this amazing stock analysis on Stockbuzz.ai!";
  const ogDescription =
    document
      .querySelector('meta[property="og:description"]')
      ?.getAttribute("content") || "";
  const textContent = `${ogTitle}${ogDescription ? " - " + ogDescription : ""}`;
  const text = encodeURIComponent(textContent);

  let shareUrl = "";
  switch (platform) {
    case "facebook":
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
      break;
    case "twitter":
      shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
      break;
    case "linkedin":
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
      break;
    default:
      return;
  }
  window.open(shareUrl, "_blank");
};

const copyLink = () => {
  navigator.clipboard.writeText(window.location.href).then(() => {
    message.success("Link copied to clipboard!");
  });
};

const ShareDropdown = (
  <Menu>
    <Menu.Item key="copy" icon={<LinkOutlined />} onClick={copyLink}>
      Copy link
    </Menu.Item>
    <Menu.Item
      key="twitter"
      icon={<TwitterOutlined />}
      onClick={() => shareOnSocial("twitter")}
    >
      Share on X
    </Menu.Item>
    <Menu.Item
      key="facebook"
      icon={<FacebookOutlined />}
      onClick={() => shareOnSocial("facebook")}
    >
      Share on Facebook
    </Menu.Item>
    <Menu.Item
      key="linkedin"
      icon={<LinkedinOutlined />}
      onClick={() => shareOnSocial("linkedin")}
    >
      Share on LinkedIn
    </Menu.Item>
  </Menu>
);

const ShareButton = ({ chatData }) => {
  return (
    <div style={styles.shareContainer}>
      <Dropdown overlay={ShareDropdown} placement="bottomRight">
        <Button
          icon={<ShareAltOutlined />}
          style={styles.shareButton}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background =
              "linear-gradient(135deg, #BBDEFB, #90CAF9)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background =
              "linear-gradient(135deg, #E3F2FD, #BBDEFB)")
          }
        >
          Share
        </Button>
      </Dropdown>
    </div>
  );
};

// Helper: Colorize transcript by speaker.
// Splits transcript into lines and if a line starts with "Speaker:", assigns a unique color.
const colorizeTranscript = (content) => {
  const lines = content.split("\n");
  const colors = ["#0D47A1", "#1976D2", "#388E3C", "#F57C00", "#D32F2F"];
  let speakerColorMap = {};
  let colorIndex = 0;

  return lines.map((line, i) => {
    const match = line.match(/^([^:]+:)/);
    if (match) {
      const speaker = match[1];
      if (!speakerColorMap[speaker]) {
        speakerColorMap[speaker] = colors[colorIndex % colors.length];
        colorIndex++;
      }
      const speakerColor = speakerColorMap[speaker];
      const restOfLine = line.substring(speaker.length);
      return (
        <p key={i} style={{ marginBottom: "12px" }}>
          <span style={{ color: speakerColor, fontWeight: "bold" }}>
            {speaker}
          </span>
          <span>{restOfLine}</span>
        </p>
      );
    } else {
      return (
        <p key={i} style={{ marginBottom: "12px" }}>
          {line}
        </p>
      );
    }
  });
};

const toTitleCasePreservingSymbols = (str, symbolUpper) => {
  return str
    .replace(/---/g, "-")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word, idx, arr) => {
      const w = word.toLowerCase();
      const prev = arr[idx - 1]?.toLowerCase();

      // Uppercase known base symbol
      if (w === symbolUpper.toLowerCase()) return symbolUpper;

      // Uppercase if part of 'vs' pattern
      if (idx > 0 && (prev === "vs" || arr[idx + 1]?.toLowerCase() === "vs")) {
        return word.toUpperCase();
      }

      // Otherwise capitalize normally
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(" ");
};

const SeoFetch = () => {
  const [chatData, setChatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState("");
  const [transcriptDrawerVisible, setTranscriptDrawerVisible] = useState(false);
  const [transcriptContent, setTranscriptContent] = useState("");
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptDate, setTranscriptDate] = useState("");

  const location = useLocation();
  const { symbol, slug } = useParams();
  // const slug = `${symbol}-${slug_part}`;

  const isEarningCallSummaryPage =
    slug && slug.includes("stock-latest-earning-call-summary");

  useEffect(() => {
    if (transcriptDrawerVisible && isEarningCallSummaryPage) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      const currentQuarter = Math.ceil(currentMonth / 3);
      const symbolFromPage = slug
        .split("-stock-latest-earning-call-summary")[0]
        .toUpperCase();
      const apiUrl =
        "https://e6lie7myp2.execute-api.us-east-1.amazonaws.com/test_deploy";

      async function fetchLatestTranscript() {
        let attempts = 0;
        const maxAttempts = 8;
        let year = currentYear;
        let quarter = currentQuarter > 1 ? currentQuarter - 1 : 4;
        if (currentQuarter === 1) {
          year = currentYear - 1;
        }

        while (attempts < maxAttempts) {
          try {
            const response = await axios.post(apiUrl, {
              queryStringParameters: {
                endpoint: "earning_call_transcript",
                symbols: symbolFromPage,
                params: `year=${year}&quarter=${quarter}`,
              },
            });
            const data = JSON.parse(response.data.body);
            if (
              data &&
              data.length > 0 &&
              data[0].content &&
              data[0].content.trim().length > 0
            ) {
              setTranscriptContent(data[0].content);
              setTranscriptDate(data[0].date || "Date not available");
              return;
            }
          } catch (error) {
            console.error(
              `Error fetching transcript for year ${year}, quarter ${quarter}:`,
              error
            );
          }
          attempts++;
          quarter--;
          if (quarter === 0) {
            quarter = 4;
            year--;
          }
        }
        setTranscriptContent("No transcript available for the current period.");
        setTranscriptDate("");
      }

      setTranscriptLoading(true);
      fetchLatestTranscript().finally(() => setTranscriptLoading(false));
    }
  }, [transcriptDrawerVisible, isEarningCallSummaryPage, slug]);

  useEffect(() => {
    if (slug) {
      // console.log("Fetching SEO for slug:", slug);
      fetchSeoData(slug);
    } else {
      // console.log("No slug found in path or query");
      setLoading(false);
      setError("No slug provided.");
    }
  }, [slug]);

  const fetchSeoData = async (pageQuery) => {
    setLoading(true);

    try {
      const response = await axios.post(
        API_URL,
        { queryStringParameters: { url: pageQuery } },
        { headers: { "Content-Type": "application/json" } }
      );
      const rawData = JSON.parse(response.data.body);

      if (!rawData?.description) {
        throw new Error("Missing description");
      }

      const sanitized = rawData.description.replace(/undefined/g, "null");
      const parsed = JSON.parse(sanitized);

      setChatData(parsed);

      if (parsed.response?.question_time) {
        setLastUpdated(
          new Date(parsed.response.question_time).toLocaleDateString("en-US", {
            weekday: "short",
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        );
      } else {
        setLastUpdated("Not Available");
      }
    } catch (err) {
      console.error("fetchSeoData error:", err);
      // immediate redirect back to the question URL
      window.location.href = `/?question=${encodeURIComponent(pageQuery)}`;
    } finally {
      setLoading(false);
    }
  };

  const MemoizedChartsAndTables = useMemo(() => {
    const { router, subrouter, entity, table_data, extracted_columns } =
      chatData?.response || {};
    if (router === "CompanyAnalysis") {
      // console.log(subrouter);

      return (
        <Card
          // title={`📊 Key Metrics for ${
          //   entity?.[0]?.toUpperCase() || "Company"
          // }`}
          bordered={false}
          style={{
            marginTop: "2rem",
            padding: "1.5rem",
            borderRadius: "16px",
            background: "#ffffff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
          }}
          headStyle={{
            fontSize: "1.2rem",
            fontWeight: 600,
            color: "#0D47A1",
          }}
        >
          {" "}
          <CompanyCharts symbols={entity} subrouter={subrouter} />{" "}
        </Card>
      );
    }
    if (router === "SQL" && table_data) {
      return (
        <Card
          title={`📈 SQL-Based Metrics for ${table_data?.join(", ")}`}
          bordered={false}
          style={{
            marginTop: "2rem",
            padding: "1.5rem",
            borderRadius: "16px",
            background: "#ffffff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
          }}
          headStyle={{
            fontSize: "1.2rem",
            fontWeight: 600,
            color: "#0D47A1",
          }}
        >
          <ChartsAndTables
            input_symbols={table_data.join(",")}
            sql_columns={extracted_columns?.split("@@@")[1]}
          />{" "}
        </Card>
      );
    }
    return null;
  }, [chatData]);

  const BlogCarousel = () => {
    const location = useLocation();
    const [swiperInstance, setSwiperInstance] = useState(null);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);

    if (!chatData?.response?.peers || chatData.response.peers.length === 0) {
      return null;
    }

    const { subrouter } = chatData.response;
    const entity = chatData?.response?.entity?.[0]?.toLowerCase(); // Original entity in lowercase
    const isFairValuation = subrouter === "fair valuation";
    const carouselTitle = isFairValuation
      ? "Intrinsic Stock Value of Competitors"
      : "Competitor Analysis";

    const currentSlug = slug?.toLowerCase();

    return (
      <div>
        <h4 style={styles.title}>{carouselTitle}</h4>
        <nav style={styles.hiddenSeoLinksContainer}>
          <ul style={styles.hiddenSeoLinksList}>
            {chatData.response.peers.map((peer, index) => {
              const peerSymbol = peer.toLowerCase();
              const peerSlug = currentSlug.replace(
                new RegExp(entity, "gi"),
                peerSymbol
              );
              const peerUrl = `https://www.stockbuzz.ai/blogs/${peerSymbol}/${peerSlug}`;

              return (
                <li key={index} style={styles.hiddenSeoLinkItem}>
                  <a href={peerUrl} style={styles.hiddenSeoLink}>
                    {peer}{" "}
                    {isFairValuation
                      ? "Intrinsic Value"
                      : "Earnings Call Summary"}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
        <Swiper
          {...swiperParams}
          modules={[Navigation]}
          onSwiper={(swiper) => {
            setSwiperInstance(swiper);
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          onSlideChange={(swiper) => {
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
        >
          {chatData.response.peers.map((peer, index) => {
            const peerSymbol = peer.toLowerCase();
            const peerSlug = currentSlug.replace(
              new RegExp(entity, "gi"),
              peerSymbol
            );
            const peerUrl = `https://www.stockbuzz.ai/blogs/${peerSymbol}/${peerSlug}`;
            const backgroundColor = pastelColors[index % pastelColors.length];

            return (
              <SwiperSlide key={index}>
                {/* <Card
                  hoverable
                  style={{
                    ...styles.card,
                    backgroundColor,
                    backgroundImage: `url(https://images.financialmodelingprep.com/symbol/${peer}.png)`,
                  }}
                  onClick={() => window.open(peerUrl, "_blank")}
                >
                  <div style={styles.overlay}></div>
                  <div style={styles.blogTitleContainer}>
                    <p style={styles.blogTitle}>{peer}</p>
                  </div>
                </Card> */}
                <Card
                  hoverable
                  style={{
                    ...styles.card,
                    background: "#626569", // ✅ Dark, finance-professional background
                    border: "1px solid rgba(255, 255, 255, 0.1)", // ✅ Light subtle border
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)", // ✅ Slightly stronger shadow
                    backdropFilter: "blur(2px)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "20px",
                  }}
                  onClick={() => window.open(peerUrl, "_blank")}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 24px rgba(0,0,0,0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 20px rgba(0,0,0,0.2)";
                  }}
                >
                  {/* Logo */}
                  <img
                    src={`https://images.financialmodelingprep.com/symbol/${peer}.png`}
                    alt={`${peer} logo`}
                    style={{
                      width: "60px",
                      height: "60px",
                      objectFit: "contain",
                      marginBottom: "12px",
                      backgroundColor: "transparent",
                    }}
                  />

                  {/* Company Symbol */}
                  <p
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: "#FFFFFF", // ✅ White text
                      margin: 0,
                    }}
                  >
                    {peer}
                  </p>
                </Card>
              </SwiperSlide>
            );
          })}
          {/* Left Arrow */}
          {!isBeginning && (
            <div
              onClick={() => swiperInstance?.slidePrev()}
              style={{
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
                background: "white",
                borderRadius: "50%",
                padding: "8px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                cursor: "pointer",
                zIndex: 10,
                opacity: 0.7,
                transition: "opacity 0.3s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
            >
              <LeftOutlined style={{ fontSize: "20px" }} />
            </div>
          )}

          {/* Right Arrow */}
          {!isEnd && (
            <div
              onClick={() => swiperInstance?.slideNext()}
              style={{
                position: "absolute",
                top: "50%",
                right: "8px",
                transform: "translateY(-50%)",
                background: "white",
                borderRadius: "50%",
                padding: "8px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                cursor: "pointer",
                zIndex: 10,
                opacity: 0.7,
                transition: "opacity 0.3s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
            >
              <RightOutlined style={{ fontSize: "20px" }} />
            </div>
          )}
        </Swiper>
      </div>
    );
  };

  const renderLLMResponse = (subrouter) => {
    if (!chatData?.response?.llm_response) return null;

    const entitiesToLink = chatData.response.entity.filter((e) => e.length > 1);

    let sanitizedMessages = chatData.response.llm_response
      .replace(/undefined>/g, "")
      .replace(/<a[^>]+href="[^"]*"[^>]*>(.*?)<\/a>/gi, "$1")
      .replace(/<\/?a[^>]*>/gi, "")
      .replace(/<h([1-6]) id="[^"]*">/gi, "<h$1>")
      .replace(/#\S+/g, "")
      .replace(/\s+/g, " ")
      .replace(/<\/?em>|<\/?i>/g, " ")
      .replace(/([a-zA-Z0-9])(<[a-zA-Z])/g, "$1 $2")
      .replace(/(<\/[a-zA-Z]>)([a-zA-Z0-9])/g, "$1 $2");

    entitiesToLink.forEach((entity) => {
      const regex = new RegExp(`\\b${entity}\\b`, "g");
      sanitizedMessages = sanitizedMessages.replace(
        regex,
        `<a href="https://www.stockbuzz.ai/${entity}" target="_blank">${entity}</a>`
      );
    });

    const formulaMatches = sanitizedMessages.match(/\$\$[\s\S]*?\$\$/g) || [];
    const sanitizedFormulas = formulaMatches.map((formula) =>
      formula.replace(/^\$\$|\$\$$/g, "").trim()
    );
    const nonFormulaContent =
      sanitizedMessages.split(/\$\$[\s\S]*?\$\$/g) || [];

    if (subrouter !== "fair valuation") {
      return (
        <Card
          style={{
            padding: "1.5rem",
            backgroundColor: "#ffffff",
            border: "1px solid #E0E0E0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            borderRadius: "12px",
            marginBottom: "2rem",
          }}
        >
          <div
            className="markdown-preview"
            style={{
              fontSize: isMobile ? "0.95rem" : "1.05rem",
              lineHeight: "1.7",
              color: "#333",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            <MarkdownPreview source={sanitizedMessages} />
          </div>
        </Card>
      );
    }

    return (
      <MathJaxContext config={mathJaxConfig}>
        <div className="html-content" style={styles.htmlContainer}>
          {nonFormulaContent.map((content, index) => (
            <div key={index} style={{ marginBottom: "2vh" }}>
              <MarkdownPreview
                source={content.trim()}
                className="markdown-preview"
              />
              {sanitizedFormulas[index] && (
                <MathJax dynamic inline={false}>
                  {`\\[${sanitizedFormulas[index]}\\]`}
                </MathJax>
              )}
            </div>
          ))}
        </div>
      </MathJaxContext>
    );
  };

  const { pathname } = useLocation();

  // console.log(chatData?.response);
  return (
    <>
      <Helmet>
        <title>{chatData?.seo_title || "Stockbuzz.ai"}</title>

        <meta
          name="description"
          content={
            chatData?.seo_description ||
            "AI-powered Stock Screener, latest company analysis, earnings reports, and undervalued stocks. Ask unlimited questions—no sign-up needed."
          }
        />
        <link rel="canonical" href={`https://www.stockbuzz.ai${pathname}`} />
        <meta
          property="og:title"
          content={chatData?.seo_title || "Stockbuzz.ai"}
        />
        <meta
          property="og:description"
          content={
            chatData?.seo_description ||
            "AI-powered Stock Screener, latest company analysis, earnings reports, and undervalued stocks."
          }
        />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://www.stockbuzz.ai/static/images/stockbuzz_og_image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={chatData?.seo_title || "Stockbuzz.ai"}
        />
        <meta
          name="twitter:description"
          content={
            chatData?.seo_description ||
            "AI-powered Stock Screener, latest company analysis, earnings reports, and undervalued stocks."
          }
        />
        <meta
          name="twitter:image"
          content="https://www.stockbuzz.ai/static/images/stockbuzz_og_image.jpg"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="https://www.stockbuzz.ai/favicon.ico" />
      </Helmet>

      <div style={styles.container}>
        {loading ? (
          <div style={styles.center}>
            <Spin size="large" />
            <Text type="secondary">Fetching content...</Text>
          </div>
        ) : error ? (
          <Text type="danger">{error}</Text>
        ) : chatData ? (
          <div>
            <Card
              style={{
                borderRadius: "1%",
                padding: ".5%",
                textAlign: "center",
                boxShadow: "none",
                border: "none",
              }}
            >
              <h1
                style={{
                  margin: "0",
                  fontWeight: "bold",
                  color: "#0D47A1",
                  textTransform: "capitalize",
                }}
              >
                {(() => {
                  let text =
                    chatData?.response?.router === "SQL"
                      ? chatData?.seo_title
                      : chatData?.message;

                  const symbols = chatData?.response?.entity || [];

                  if (text && Array.isArray(symbols)) {
                    symbols.forEach((symbol) => {
                      const regex = new RegExp(`\\b${symbol}\\b`, "i");
                      text = text.replace(regex, "");
                    });

                    // Remove empty brackets like () or [] after symbol removal
                    text = text.replace(/\(\s*\)|\[\s*\]/g, "");

                    // Remove double spaces left behind
                    text = text.replace(/\s{2,}/g, " ").trim();
                  }

                  return text;
                })()}
              </h1>
            </Card>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "0.75rem", // 👈 Space between label and button
                marginTop: "1.5rem",
                marginBottom: "1rem",
                paddingRight: isMobile ? "1rem" : "2rem", // 👈 adds slight indent on right
                flexWrap: "wrap",
              }}
            >
              <Text
                type="secondary"
                style={{
                  fontSize: "0.9rem",
                  color: "#607D8B",
                  fontWeight: 500,
                }}
              >
                Last updated: {lastUpdated}
              </Text>
              {chatData?.response?.router !== "SQL" && (
                <Button
                  // type="default"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    const question = chatData.message;
                    const newUrl = `${
                      window.location.origin
                    }/?question=${encodeURIComponent(question)}`;
                    window.open(newUrl, "_blank");
                  }}
                  style={{
                    padding: "0.5rem 1rem",
                    backgroundColor: "#E3F2FD",
                    color: "#0D47A1",
                    border: "1px solid #BBDEFB",
                    borderRadius: "999px",
                    fontWeight: "600",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    fontSize: "0.9rem",
                    transition: "all 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#BBDEFB")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#E3F2FD")
                  }
                >
                  Refresh
                </Button>
              )}
            </div>
            <div>
              <div style={{ marginTop: "2%" }}>
                <ClickableTags tags={chatData?.response?.tags || []} />
              </div>

              {/* Display LLM Response Summary */}
              {chatData?.response?.llm_response_summary && (
                <Card
                  style={{
                    marginBottom: "2rem",
                    padding: "1.5rem",
                    borderRadius: "12px",
                    backgroundColor: "#FAFAFA",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                    border: "1px solid #E0E0E0",
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? "1.1rem" : "1.3rem",
                      fontWeight: "700",
                      color: "#0D47A1",
                      marginBottom: "1rem",
                      borderBottom: "2px solid #BBDEFB",
                      paddingBottom: "0.3rem",
                    }}
                  >
                    📌 Overall Takeaways
                  </div>

                  <div
                    dangerouslySetInnerHTML={{
                      __html: chatData.response.llm_response_summary,
                    }}
                    style={{
                      fontSize: isMobile ? "0.95rem" : "1rem",
                      lineHeight: "1.8",
                      color: "#333",
                      paddingLeft: "1rem",
                    }}
                  ></div>
                </Card>
              )}
              {isEarningCallSummaryPage && (
                <div style={{ marginTop: "1rem", textAlign: "left" }}>
                  <Button
                    icon={<FileTextOutlined style={{ fontSize: "1.2rem" }} />}
                    onClick={() => setTranscriptDrawerVisible(true)}
                    style={{
                      background: "linear-gradient(135deg, #E3F2FD, #BBDEFB)",
                      color: "#0D47A1",
                      border: "1px solid #90CAF9",
                      borderRadius: "999px",
                      padding: "0.75rem 1.6rem",
                      fontSize: "1rem",
                      fontWeight: "600",
                      boxShadow: "0 4px 10px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background =
                        "linear-gradient(135deg, #BBDEFB, #90CAF9)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        "linear-gradient(135deg, #E3F2FD, #BBDEFB)")
                    }
                  >
                    Full Earning Call Transcript
                  </Button>

                  <Drawer
                    title={
                      transcriptDate
                        ? `Earning Call Transcript - ${transcriptDate}`
                        : "Earning Transcript"
                    }
                    placement="right"
                    onClose={() => setTranscriptDrawerVisible(false)}
                    visible={transcriptDrawerVisible}
                    width={isMobile ? "90%" : "50%"}
                  >
                    {transcriptLoading ? (
                      <div style={{ textAlign: "center" }}>
                        <Text>Fetching latest Transcript...</Text>
                        <Spin
                          size="large"
                          style={{ display: "block", margin: "2rem auto" }}
                        />
                      </div>
                    ) : (
                      transcriptContent && colorizeTranscript(transcriptContent)
                    )}
                  </Drawer>
                </div>
              )}
              <div style={{ marginTop: "1rem" }}>
                {renderLLMResponse(chatData?.response?.subrouter)}
              </div>
            </div>

            {chatData.response?.sql_code && (
              <div>
                <Card
                  style={{
                    borderRadius: "1%",
                    padding: ".5%",
                    textAlign: "left",
                    background: "transparent",
                    boxShadow: "none",
                    border: "none",
                  }}
                >
                  <h2
                    style={{
                      margin: "0",
                      fontWeight: "bold",
                      color: "grey",
                      textTransform: "capitalize",
                    }}
                  >
                    {chatData.message}
                  </h2>
                </Card>
                <Card style={styles.sqlCard}>
                  <Text strong>Executed SQL Query:</Text>
                  <pre style={styles.sqlBlock}>
                    {formatSQLQuery(chatData.response?.sql_code)}
                  </pre>
                </Card>
              </div>
            )}
            {chatData?.response?.faq_headline && (
              <FaqCollapse faqContent={chatData.response.faq_headline} />
            )}

            {MemoizedChartsAndTables}

            {chatData?.response?.subrouter !== "fair valuation" &&
              chatData?.response?.relevant_questions?.match(
                /<related_question\d+>(.*?)<\/related_question\d+>/g
              )?.length >= 3 && (
                <Card
                  style={{
                    marginTop: "3.5rem",
                    marginBottom: "3rem",
                    padding: "0",
                    borderRadius: "1.25rem",
                    border: "1px solid rgba(229, 231, 235, 0.5)",
                    background: "rgba(255, 255, 255, 0.75)",
                    boxShadow: "0 0.75rem 1.875rem rgba(0, 0, 0, 0.05)",
                    backdropFilter: "blur(0.75rem)",
                    overflow: "hidden",
                  }}
                  bodyStyle={{ padding: "2rem" }}
                >
                  <div style={{ padding: "1.5rem" }}>
                    <h3
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: 800,
                        color: "#111827",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        marginBottom: "0.625rem",
                      }}
                    >
                      📌 Related Questions
                    </h3>
                    <p
                      style={{
                        color: "#6B7280",
                        fontSize: "0.9375rem",
                        marginBottom: "1.875rem",
                      }}
                    >
                      Tap into what other investors are asking.
                    </p>
                    <List
                      dataSource={
                        chatData.response.relevant_questions
                          ?.match(/<[^>]+>([^<]+)<\/[^>]+>/g)
                          ?.map((q) =>
                            q
                              .replace(/<[^>]+>/g, "")
                              .trim()
                              .replace(/\b\w/g, (char) => char.toUpperCase())
                          ) || []
                      }
                      renderItem={(item) => (
                        <List.Item
                          style={styles.questionItem}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f0f0f0";
                            e.currentTarget.style.transform = "scale(1.02)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#ffffff";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={() => {
                            const baseUrl = "https://www.stockbuzz.ai/";
                            const questionParam = `?question=${encodeURIComponent(
                              item
                            )}`;
                            window.open(baseUrl + questionParam, "_blank");
                          }}
                        >
                          <span style={styles.questionLink}>{item}</span>
                        </List.Item>
                      )}
                    />
                  </div>
                </Card>
              )}

            <ShareButton />
            <BlogCarousel />
            <BlogCarouselContainer />
          </div>
        ) : null}
      </div>
    </>
  );
};

const styles = {
  container: {
    maxWidth: isMobile ? "100vw" : "90vw",
    padding: isMobile ? ".1vw" : "1vw",
    marginBottom: isMobile ? "20vw" : "5vw",
    marginTop: isMobile ? "10vw" : "2vw",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "2vh",
  },
  refreshContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "0.5vh",
  },
  lastUpdated: {
    fontSize: "0.85rem",
    color: "#777",
    marginBottom: "0.3vh",
  },
  refreshButton: {
    fontSize: "0.9rem",
    padding: "0.7vh 1vw",
    backgroundColor: "#f0f2f5",
    border: "0.1vw solid #d9d9d9",
    borderRadius: "0.4vw",
    transition: "background-color 0.3s ease, transform 0.2s ease",
    cursor: "pointer",
  },
  timestamp: {
    fontSize: "0.9rem",
    color: "#555",
    marginBottom: "2vh",
    display: "block",
  },
  markdownContainer: {
    width: "100%",
    overflowX: "hidden",
    wordWrap: "break-word",
    padding: "1%",
    backgroundColor: "#f9f9f9",
    borderRadius: "1vw",
    border: "0.1vw solid #ddd",
  },
  sqlCard: {
    marginTop: "0vh",
    padding: "2%",
    backgroundColor: "#F8F9FC",
    borderRadius: "0.5vw",
    boxShadow: "0 0.5vw 1vw rgba(0, 0, 0, 0.1)",
  },
  sqlBlock: {
    backgroundColor: "#1E1E1E",
    color: "#FFF",
    padding: "2%",
    borderRadius: "0.5vw",
    fontSize: "1rem",
    fontFamily: "'Fira Code', monospace",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
  },
  tagsContainer: {
    marginTop: isMobile ? "10%" : "1%",
    marginBottom: isMobile ? "5%" : "1%",
    padding: ".5%",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center", // better symmetry
  },
  tagsTitle: {
    fontSize: isMobile ? "xsmall" : "medium",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "1vh",
  },
  tag: {
    fontSize: isMobile ? "0.75rem" : "0.85rem",
    padding: "0.4rem 0.8rem",
    fontWeight: 600,
    color: "#0D47A1",
    backgroundColor: "#E3F2FD",
    border: "1px solid #90CAF9",
    borderRadius: "20px",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
  },
  relatedQuestionsCard: {
    marginTop: "0%",
    padding: "0%",
    backgroundColor: "#ffffff",
    borderRadius: ".4vw",
    boxShadow: "0 0.3vw 0.6vw rgba(0, 0, 0, 0.1)",
  },
  relatedQuestionsTitle: {
    fontSize: "1.1rem",
    fontWeight: "bold",
    color: "#333",
    marginBottom: ".4vh",
    marginTop: ".1vh",
  },
  questionItem: {
    padding: "1vh 2vw",
    borderRadius: "1vw",
    transition: "background-color 0.3s ease, transform 0.2s ease",
    cursor: "pointer",
  },
  questionLink: {
    fontSize: "1rem",
    fontWeight: "500",
    color: "#0073e6",
    textDecoration: "none",
    transition: "color 0.3s ease",
  },
  continueConversationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "4vh",
  },
  continueButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5vw",
    fontSize: "1rem",
    padding: "1.2vh 3vw",
    backgroundColor: "#B3D9FF",
    border: "none",
    borderRadius: "0.5vw",
    color: "#003366",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    boxShadow: "0 0.4vw 0.8vw rgba(179, 217, 255, 0.5)",
  },
  continueButtonHover: {
    backgroundColor: "#A4C8F0",
  },
  title: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    textAlign: "left",
    marginBottom: "2vh",
  },
  card: {
    height: "20vh",
    width: isMobile ? "90%" : "auto",
    margin: isMobile ? "0 auto" : "unset",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    borderRadius: "1vw",
    boxShadow: "0 0.4vw 1vw rgba(0, 0, 0, 0.1)",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    padding: "1vh 1vw",
    backgroundSize: "30%",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    position: "relative",
    overflow: "hidden",
  },
  blogTitleContainer: {
    position: "absolute",
    bottom: "0vh",
    left: "50%",
    transform: "translateX(-50%)",
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },
  blogTitle: {
    fontSize: "1rem",
    fontWeight: "600",
    color: "#333",
    textAlign: "center",
    padding: "0.5vh 1vw",
    background: "rgba(255, 255, 255, 0.55)",
    borderRadius: "0.5vw",
    width: "80%",
  },
  overlay: {
    position: "absolute",
    top: "5vw",
    left: "5vw",
    background: "rgba(255, 255, 255, 0.5)",
    borderRadius: "inherit",
  },
  hiddenSeoLinksContainer: {
    position: "absolute",
    left: "-9999px",
    width: "1px",
    height: "1px",
    overflow: "hidden",
    opacity: 0,
  },
  hiddenSeoLinksList: {
    listStyle: "none",
    padding: 0,
  },
  hiddenSeoLinkItem: {
    marginBottom: "0.5vh",
  },
  hiddenSeoLink: {
    fontSize: "0.8rem",
    color: "#0073e6",
    textDecoration: "none",
  },
  shareContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "0.3rem",
    width: "100%",
  },
  // shareButton: {
  //   fontSize: "1rem",
  //   padding: ".8vh .8vw",
  //   backgroundColor: "#E3F2FD",
  //   borderRadius: ".8vw",
  //   border: "none",
  //   color: "#0D47A1",
  //   fontWeight: "bold",
  //   cursor: "pointer",
  //   transition: "all 0.3s ease-in-out",
  // },
  shareButton: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "0.95rem",
    padding: "0.6rem 1.2rem",
    background: "linear-gradient(135deg, #E3F2FD, #BBDEFB)",
    borderRadius: "30px",
    border: "1px solid #90CAF9",
    color: "#0D47A1",
    fontWeight: 600,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
  },
};

export default SeoFetch;
